import React, { ReactElement } from 'react';
import { gql } from 'apollo-boost';
import { useQuery } from '@apollo/react-hooks';
import { Box, Text, Flex } from '@chakra-ui/core';
import { s3BucketUrl } from 'config';
import { useIsThemeBreakPoint } from 'contexts/Breakpoints';
import { BucketItem } from 'typings';
import { DownloadSVG } from 'components/Icons';
import _ from 'lodash';

export const makeUrl = (slug: string, year: number, file?: string): string => {
  return `${s3BucketUrl}/${slug}/${year}/${file}`;
};

const GET_BUCKET_CONTENTS = gql`
  query getBucketContents($slug: String!, $year: Int!) {
    getBucketContents(slug: $slug, year: $year) {
      items {
        filename
      }
    }
  }
`;

interface DataBookYearDownloadsProps {
  year: number;
  slug: string;
  /** e.g. accountability-style spacing after Last Updated */
  marginTop?: string;
  /** HIDOE Data Book: main PDF is in the viewer; only show Appendix download tiles. */
  appendixOnly?: boolean;
}

const DataBookYearDownloads: React.FC<DataBookYearDownloadsProps> = ({
  year,
  slug,
  marginTop,
  appendixOnly,
}): ReactElement => {
  const { data } = useQuery(GET_BUCKET_CONTENTS, {
    variables: { slug, year },
  });
  const laptop = useIsThemeBreakPoint(1);

  let items = data?.getBucketContents?.items.map((i: any) => {
    return {
      ...i,
      ...makeButtonTextFromFilename(i),
    };
  });

  if (appendixOnly && items) {
    items = items.filter((row: { filename: string }) => isAppendixFilename(row.filename));
  }

  items = _.orderBy(items, ['order']);

  return (
    <Flex
      marginTop={marginTop}
      flexDirection={laptop ? 'row' : 'column'}
      flexWrap={'wrap'}
      alignItems={laptop === false ? 'center' : ''}
    >
      {items &&
        items.map(
          (item: BucketItem): ReactElement => (
            <DownloadReportButton key={item.filename} slug={slug} year={year} item={item as any} />
          ),
        )}
    </Flex>
  );
};

function isAppendixFilename(filename: string): boolean {
  return filename.includes('AppendixCData') || filename.includes('AppendixData');
}

function makeButtonTextFromFilename(i: any) {
  let text;
  let order;
  if (isAppendixFilename(i.filename)) {
    text = 'Appendix C Data Table';
    order = 2;
  } else if (i.filename.includes('SuptRpt')) {
    text = `Superintendent's Annual Report`;
    order = 1;
  } else if (i.filename.includes('HIDOEDatabook')) {
    text = `HIDOE Databook`;
    order = 1;
  } else {
    text = i.filename;
    order = 99;
  }

  return { text, order };
}

interface DownloadButtonProps {
  slug: string;
  year: number;
  item: BucketItem & { text: string };
}

const DownloadReportButton: React.FC<DownloadButtonProps> = ({ slug, year, item }): ReactElement => {
  return (
    <a href={makeUrl(slug, year, item.filename)} target="_blank" rel="noopener noreferrer">
      <Box
        marginRight="16px"
        marginBottom="16px"
        border="1px solid"
        borderRadius="8px"
        borderColor="primary"
        padding="12px"
        maxWidth="350px"
      >
        <Flex align="center">
          <Box flex="1">
            <DownloadSVG color="black" />
          </Box>
          <Flex flex="4" padding="16px" flexDir="column" justify="space-between">
            <Text color="primary" fontWeight="bold">
              DOWNLOAD
            </Text>
            <Text color="primary">{item.text}</Text>
          </Flex>
        </Flex>
      </Box>
    </a>
  );
};

export default DataBookYearDownloads;
